import React from 'react';

import { Text } from '../../../../components/Text';
import { languageName } from '../../../../config/languageEndonyms';
import { Root } from './LanguagePicker.styles';

/** Emoji flag per language id, aligned with `window.config.languages`; an unknown id falls
 *  back to the globe. Display names come from `languageName` (endonyms), not from here.
 *  `cen` and `cfr` are both Canada, so both use the Canadian flag. */
const FLAGS: Record<string, string> = {
  en: '🇬🇧',
  cen: '🇨🇦',
  mx: '🇲🇽',
  cl: '🇨🇱',
  co: '🇨🇴',
  pe: '🇵🇪',
  ve: '🇻🇪',
  za: '🇿🇦',
  bra: '🇧🇷',
  arg: '🇦🇷',
  cfr: '🇨🇦',
};

export type LanguagePickerProps = {
  /** Language ids to offer (short codes, e.g. ['en', 'mx']). */
  languages: string[];
  /** Currently active language id (short code; already locale-normalised). */
  currentLanguage: string;
  /** Called with the chosen language id. */
  onChange: (id: string) => void;
};

/**
 * Login language switcher — a self-contained, presentational dropdown (no Redux),
 * mirroring the config-driven `module.languageSwitcher` node tree (label · trigger
 * pill · upward popover list). Kept OUT of the Redux-connected page so it can be
 * mounted and unit-tested in isolation (`LanguagePicker.ct.tsx`).
 *
 * NOTE: the popover uses `language-popover`, NOT `dropdown-menu` — the latter
 * collides with Bootstrap 5's global `.dropdown-menu { display: none }` (revealed
 * only via a `.show` class, which reactstrap adds but this hand-written dropdown
 * does not), which would render the menu invisible. The CT spec guards this.
 */
export const LanguagePicker = ({ languages, currentLanguage, onChange }: LanguagePickerProps): React.ReactElement => {
  const [open, setOpen] = React.useState<boolean>(false);
  const rootRef = React.useRef<HTMLDivElement | null>(null);

  // Endonyms: each language shown in its OWN tongue (the same regardless of the active UI
  // language), rendered raw so i18n does not re-translate them. See `useLanguageSwitcher`.
  const nameOf = (id: string): string => languageName(id);

  // Close the popover on an outside click.
  React.useEffect(() => {
    if (!open) return undefined;
    const onDocClick = (event: MouseEvent): void => {
      if (rootRef.current && !rootRef.current.contains(event.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, [open]);

  const select = React.useCallback(
    (id: string): void => {
      onChange(id);
      setOpen(false);
    },
    [onChange],
  );

  // Enter / Space activate a role="button" element (keyboard parity with the click).
  const onKey =
    (action: () => void) =>
    (event: React.KeyboardEvent): void => {
      if (event.key === 'Enter' || event.key === ' ') {
        event.preventDefault();
        action();
      }
    };

  const toggle = (): void => setOpen((v) => !v);

  return (
    <Root className="language-switcher" ref={rootRef}>
      <Text className="language-switcher-label" text="Choose language" />
      <div className="language-dropdown">
        <div
          className="language-trigger"
          role="button"
          tabIndex={0}
          aria-haspopup="listbox"
          aria-expanded={open}
          onClick={toggle}
          onKeyDown={onKey(toggle)}
        >
          <span className="flag">{FLAGS[currentLanguage] || '🌐'}</span>
          <Text className="name" text={nameOf(currentLanguage)} raw />
          <i className="bi bi-chevron-down caret" aria-hidden="true" />
        </div>

        {open && (
          <div className="language-popover">
            <div className="language-menu">
              <div className="languages">
                {languages.map((id) => (
                  <div
                    key={id}
                    className={`language-option${id === currentLanguage ? ' active' : ''}`}
                    data-language={id}
                    role="button"
                    tabIndex={0}
                    onClick={() => select(id)}
                    onKeyDown={onKey(() => select(id))}
                  >
                    <span className="flag">{FLAGS[id] || '🌐'}</span>
                    <Text className="name" text={nameOf(id)} raw />
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
      </div>
    </Root>
  );
};

export default LanguagePicker;
